<template>
  <div class="two-column-table">
    <h2 v-if="title">{{ title }}</h2>
    <ul data-cy="two-column-table-unordered-list">
      <li v-for="row in tableRows" :key="row">
        <template v-if="isLoading">
          <SkeleLoader :lines="1" contentIsFullWidth :key="row" />
        </template>

        <template v-else>
          <p v-html="row.label"></p>
          <div>
            <p v-html="row.value"></p>
            <Copy
              v-if="row.copy"
              id="copy-clipboard"
              @click="copyToClipboard(row.value)"
            />
          </div>
        </template>
      </li>
    </ul>
    <div class="two-column-table__bottom" v-if="!!slot.default">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { useSlots } from "vue";
import { copyText } from "vue3-clipboard";
import { useSnackbar } from "@/packages/vue3-snackbar";
import Copy from "@/components/icons/Copy.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";
import ClipboardCopyButton from "@/components/ui/buttons/ClipboardCopyButton.vue";

const snackbar = useSnackbar();
const copyToClipboard = (copiedValue) => {
  copyText(copiedValue, undefined, () => {});
  snackbar.add({
    type: "success",
    text: "Copied to clipboard",
    dismissible: false,
  });
};

const slot = useSlots();

/**
  * Here, tableData is expected in the below format
  * 
  * tableRows: {
    amountPaid: {
      label:'Amount to pay',
      value: null,
    },
    billedCustomer: {
      label:'Deposit reference',
      value: null,
    },
  },
  */

defineProps({
  tableRows: {
    type: Object,
  },
  title: {
    type: String,
  },
  isLoading: { type: Boolean, default: false },
});
</script>

<style lang="scss">
.two-column-table {
  background: rgba(255, 255, 255, 0.08);
  padding: 2rem;
  margin-bottom: 3.4rem;
  border-radius: 1.6rem;

  &__bottom {
    border-top: 1px solid rgba(255, 255, 255, 0.07);
    padding-top: 1.4rem;
    margin-top: 1.4rem;
  }

  h2 {
    font-size: 1.8rem;
    font-weight: 800;
    text-align: center;
    margin-bottom: 3.2rem;
    color: var(--secondary);
  }

  ul {
    width: 100%;

    li {
      width: 100%;
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      padding-bottom: 1.4rem;
      margin-bottom: 1.4rem;

      &:last-child {
        margin-bottom: 0;
        border-bottom: none;
        padding-bottom: 0;
      }

      @include breakpoint($min-xs) {
        flex-direction: row;
        column-gap: 2rem;
      }

      p {
        &:last-child {
          font-weight: 800;
          text-align: end;
          flex-shrink: 5;
        }
      }

      div {
        display: flex;
        flex-shrink: 10;

        #copy-clipboard {
          cursor: pointer;
          margin-left: 1rem;
        }
      }

      @media (max-width: 800px) {
        flex-direction: column;

        div {
          display: flex;
          width: 100%;

          p {
            padding-top: 0.2em;
            font-weight: 800;
          }
        }

        p {
          flex: 90%;
        }

        p {
          &:last-child {
            text-align: start;
            flex-shrink: 5;
            line-height: 1.5em;
            align-content: flex-start;
          }
        }

        #copy-clipboard {
          cursor: pointer;
          flex: 10%;
          align-content: flex-end;
        }
      }
    }
  }
}
</style>
